import {
    INSTAGRAM_METADATA_BY_KEY,
    TIKTOK_METADATA_BY_KEY,
} from 'utils/metrics';
import {
    getTsBrandKeywords as getGoogleTsBrandKeywords,
    getTsCategoryKeywords as getGoogleTsCategoryKeywords,
} from 'utils/api/google.api';
import {
    getTsBrandHashtags as getInstagramTsBrandHashtags,
    getTsBrandPosts as getInstagramTsBrandPosts,
    getTsCategoryHashtags as getInstagramTsCategoryHashtags,
    getTsCategoryPosts as getInstagramTsCategoryPosts,
    getTsMonthlyBrandHashtags as getInstagramTsMonthlyBrandHashtags,
    getTsMonthlyBrandPosts as getInstagramTsMonthlyBrandPosts,
    getTsMonthlyCategoryHashtags as getInstagramTsMonthlyCategoryHashtags,
    getTsMonthlyCategoryPosts as getInstagramTsMonthlyCategoryPosts,
} from 'utils/api/instagram.api';
import {
    getTsBrandHashtags as getTikTokTsBrandHashtags,
    getTsBrandPosts as getTikTokTsBrandPosts,
    getTsCategoryHashtags as getTikTokTsCategoryHashtags,
    getTsCategoryPosts as getTikTokTsCategoryPosts,
    getTsMonthlyBrandHashtags as getTikTokTsMonthlyBrandHashtags,
    getTsMonthlyBrandPosts as getTikTokTsMonthlyBrandPosts,
    getTsMonthlyCategoryHashtags as getTikTokTsMonthlyCategoryHashtags,
    getTsMonthlyCategoryPosts as getTikTokTsMonthlyCategoryPosts,
} from 'utils/api/tiktok.api';

export const DRIVERS_SOURCE = {
    TIKTOK: 'tiktok',
    INSTAGRAM: 'instagram',
    GOOGLE: 'google',
    POP_INDEX_TIKTOK: 'pop_index_tiktok',
    POP_INDEX_INSTAGRAM: 'pop_index_instagram',
    POP_INDEX_GOOGLE: 'pop_index_google',
};

export const DEFAULT_DRIVERS_SOURCE = DRIVERS_SOURCE.TIKTOK;

const SHARED_POST_COLUMNS = [
    { key: 'url', label: 'Post' },
    { key: 'non_cum_increase_WoW', label: 'Increase WoW' },
    { key: 'create_timestamp', label: 'Posted' },
];

const DRIVERS_SOURCE_CONFIG = {
    [DRIVERS_SOURCE.TIKTOK]: {
        sourceName: 'TikTok',
        metricLabel: 'views',
        currentVolumeLabel: 'Views',
        hashtagMetricLabel: 'Views',
        primaryListTitle: 'Top Hashtags',
        primaryColumnLabel: 'Hashtag',
        primaryItemLabelPlural: 'hashtags',
        primaryExportSuffix: 'hashtag',
        changeDataKey: 'non_cum_increase_WoW',
        changeLabel: 'Increase WoW',
        changePeriodLabel: 'week-over-week',
        dateRangeType: 'weekly',
        supportsPosts: true,
        showFlagging: true,
        exportPrefix: 'tiktok',
        includeCategoryForBrandRequest: false,
        api: {
            getTsCategoryHashtags: getTikTokTsCategoryHashtags,
            getTsBrandHashtags: getTikTokTsBrandHashtags,
            getTsCategoryPosts: getTikTokTsCategoryPosts,
            getTsBrandPosts: getTikTokTsBrandPosts,
        },
        paidTypesTooltipKeys: {
            paidAndPartner: 'isBrandPartnerAndPaidPartnership',
            paidOnly: 'isOnlyPaidPartnership',
            partnerOnly: 'isOnlyBrandPartner',
        },
        postMetrics: [
            [TIKTOK_METADATA_BY_KEY.non_cum_increase_WoW, true],
            [TIKTOK_METADATA_BY_KEY.last_week_views, true],
            [TIKTOK_METADATA_BY_KEY.total_post_views, false],
            [TIKTOK_METADATA_BY_KEY.non_cum_WoW, false],
            [TIKTOK_METADATA_BY_KEY.create_timestamp, false],
        ],
        postColumns: [
            ...SHARED_POST_COLUMNS.slice(0, 2),
            { key: 'author', label: 'Author' },
            {
                key: 'sentiment_summary',
                label: 'Sentiment Summary (AI-generated)',
            },
            { key: 'sentiment_score', label: 'Sentiment' },
            { key: 'last_week_views', label: 'Views' },
            { key: 'total_post_views', label: 'Total Views' },
            { key: 'non_cum_WoW', label: 'WoW' },
            SHARED_POST_COLUMNS[2],
        ],
    },
    [DRIVERS_SOURCE.INSTAGRAM]: {
        sourceName: 'Instagram',
        metricLabel: 'likes',
        currentVolumeLabel: 'Likes',
        hashtagMetricLabel: 'Likes',
        primaryListTitle: 'Top Hashtags',
        primaryColumnLabel: 'Hashtag',
        primaryItemLabelPlural: 'hashtags',
        primaryExportSuffix: 'hashtag',
        changeDataKey: 'non_cum_increase_WoW',
        changeLabel: 'Increase WoW',
        changePeriodLabel: 'week-over-week',
        dateRangeType: 'weekly',
        supportsPosts: true,
        showFlagging: true,
        exportPrefix: 'instagram',
        includeCategoryForBrandRequest: true,
        api: {
            getTsCategoryHashtags: getInstagramTsCategoryHashtags,
            getTsBrandHashtags: getInstagramTsBrandHashtags,
            getTsCategoryPosts: getInstagramTsCategoryPosts,
            getTsBrandPosts: getInstagramTsBrandPosts,
        },
        paidTypesTooltipKeys: {
            paidAndPartner: 'isBrandPartnerAndPaidPartnershipInstagram',
            paidOnly: 'isOnlyPaidPartnershipInstagram',
            partnerOnly: 'isOnlyBrandPartnerInstagram',
        },
        postMetrics: [
            [INSTAGRAM_METADATA_BY_KEY.non_cum_increase_WoW, true],
            [INSTAGRAM_METADATA_BY_KEY.last_week_likes, true],
            [INSTAGRAM_METADATA_BY_KEY.total_post_likes, false],
            [INSTAGRAM_METADATA_BY_KEY.wow, false],
            [INSTAGRAM_METADATA_BY_KEY.create_timestamp, false],
        ],
        postColumns: [
            ...SHARED_POST_COLUMNS.slice(0, 2),
            { key: 'last_week_likes', label: 'Likes' },
            { key: 'total_post_likes', label: 'Total Likes' },
            { key: 'wow', label: 'WoW' },
            SHARED_POST_COLUMNS[2],
        ],
    },
    [DRIVERS_SOURCE.GOOGLE]: {
        sourceName: 'Google',
        metricLabel: 'searches',
        currentVolumeLabel: 'Searches',
        hashtagMetricLabel: 'Searches',
        primaryListTitle: 'Top Search Queries',
        primaryColumnLabel: 'Search Query',
        primaryItemLabelPlural: 'search queries',
        primaryListColumns: 1,
        primaryItemMaxWidthClass: 'max-w-[320px]',
        primaryExportSuffix: 'keyword',
        changeDataKey: 'non_cum_increase_MoM',
        changeLabel: 'Increase MoM',
        changePeriodLabel: 'month-over-month',
        dateRangeType: 'monthly',
        supportsPosts: false,
        showFlagging: false,
        exportPrefix: 'google',
        includeCategoryForBrandRequest: true,
        api: {
            getTsCategoryHashtags: getGoogleTsCategoryKeywords,
            getTsBrandHashtags: getGoogleTsBrandKeywords,
        },
        postMetrics: [],
        postColumns: [],
    },
    [DRIVERS_SOURCE.POP_INDEX_TIKTOK]: {
        sourceName: 'TikTok',
        metricLabel: 'views',
        currentVolumeLabel: 'Views',
        hashtagMetricLabel: 'Views',
        primaryListTitle: 'Top Hashtags',
        primaryColumnLabel: 'Hashtag',
        primaryItemLabelPlural: 'hashtags',
        primaryExportSuffix: 'hashtag',
        changeDataKey: 'non_cum_increase_MoM',
        changeLabel: 'Increase MoM',
        changePeriodLabel: 'month-over-month',
        periodRatioLabel: 'MoM',
        dateRangeType: 'monthly',
        supportsPosts: true,
        showFlagging: false,
        exportPrefix: 'pop-index-tiktok',
        includeCategoryForBrandRequest: false,
        api: {
            getTsCategoryHashtags: getTikTokTsMonthlyCategoryHashtags,
            getTsBrandHashtags: getTikTokTsMonthlyBrandHashtags,
            getTsCategoryPosts: getTikTokTsMonthlyCategoryPosts,
            getTsBrandPosts: getTikTokTsMonthlyBrandPosts,
        },
        paidTypesTooltipKeys: {
            paidAndPartner: 'isBrandPartnerAndPaidPartnership',
            paidOnly: 'isOnlyPaidPartnership',
            partnerOnly: 'isOnlyBrandPartner',
        },
        postMetrics: [
            [TIKTOK_METADATA_BY_KEY.non_cum_increase_WoW, true],
            [TIKTOK_METADATA_BY_KEY.last_week_views, true],
            [TIKTOK_METADATA_BY_KEY.total_post_views, false],
            [TIKTOK_METADATA_BY_KEY.non_cum_WoW, false],
            [TIKTOK_METADATA_BY_KEY.create_timestamp, false],
        ],
        postColumns: [
            SHARED_POST_COLUMNS[0],
            { key: 'non_cum_increase_WoW', label: 'Increase MoM' },
            { key: 'author', label: 'Author' },
            { key: 'last_week_views', label: 'Views' },
            { key: 'total_post_views', label: 'Total Views' },
            { key: 'non_cum_WoW', label: 'MoM' },
            SHARED_POST_COLUMNS[2],
        ],
    },
    [DRIVERS_SOURCE.POP_INDEX_INSTAGRAM]: {
        sourceName: 'Instagram',
        metricLabel: 'views',
        currentVolumeLabel: 'Views',
        hashtagMetricLabel: 'Views',
        primaryListTitle: 'Top Hashtags',
        primaryColumnLabel: 'Hashtag',
        primaryItemLabelPlural: 'hashtags',
        primaryExportSuffix: 'hashtag',
        changeDataKey: 'non_cum_increase_MoM',
        changeLabel: 'Increase MoM',
        changePeriodLabel: 'month-over-month',
        periodRatioLabel: 'MoM',
        dateRangeType: 'monthly',
        supportsPosts: true,
        showFlagging: false,
        exportPrefix: 'pop-index-instagram',
        includeCategoryForBrandRequest: true,
        api: {
            getTsCategoryHashtags: getInstagramTsMonthlyCategoryHashtags,
            getTsBrandHashtags: getInstagramTsMonthlyBrandHashtags,
            getTsCategoryPosts: getInstagramTsMonthlyCategoryPosts,
            getTsBrandPosts: getInstagramTsMonthlyBrandPosts,
        },
        paidTypesTooltipKeys: {
            paidAndPartner: 'isBrandPartnerAndPaidPartnershipInstagram',
            paidOnly: 'isOnlyPaidPartnershipInstagram',
            partnerOnly: 'isOnlyBrandPartnerInstagram',
        },
        postMetrics: [
            [INSTAGRAM_METADATA_BY_KEY.non_cum_increase_WoW, true],
            [INSTAGRAM_METADATA_BY_KEY.last_week_likes, true],
            [INSTAGRAM_METADATA_BY_KEY.total_post_likes, false],
            [INSTAGRAM_METADATA_BY_KEY.wow, false],
            [INSTAGRAM_METADATA_BY_KEY.create_timestamp, false],
        ],
        postColumns: [
            SHARED_POST_COLUMNS[0],
            { key: 'non_cum_increase_WoW', label: 'Increase MoM' },
            { key: 'last_week_likes', label: 'Likes' },
            { key: 'total_post_likes', label: 'Total Likes' },
            { key: 'wow', label: 'MoM' },
            SHARED_POST_COLUMNS[2],
        ],
    },
    [DRIVERS_SOURCE.POP_INDEX_GOOGLE]: {
        sourceName: 'Google',
        metricLabel: 'searches',
        currentVolumeLabel: 'Searches',
        hashtagMetricLabel: 'Searches',
        primaryListTitle: 'Top Search Queries',
        primaryColumnLabel: 'Search Query',
        primaryItemLabelPlural: 'search queries',
        primaryListColumns: 1,
        primaryItemMaxWidthClass: 'max-w-[320px]',
        primaryExportSuffix: 'keyword',
        changeDataKey: 'non_cum_increase_MoM',
        changeLabel: 'Increase MoM',
        changePeriodLabel: 'month-over-month',
        dateRangeType: 'monthly',
        supportsPosts: false,
        showFlagging: false,
        exportPrefix: 'pop-index-google',
        includeCategoryForBrandRequest: true,
        api: {
            getTsCategoryHashtags: getGoogleTsCategoryKeywords,
            getTsBrandHashtags: getGoogleTsBrandKeywords,
        },
        postMetrics: [],
        postColumns: [],
    },
};

export const normalizeDriversSource = (dataSource) => {
    const normalized = String(
        dataSource || DEFAULT_DRIVERS_SOURCE,
    ).toLowerCase();
    return normalized in DRIVERS_SOURCE_CONFIG
        ? normalized
        : DEFAULT_DRIVERS_SOURCE;
};

export const getDriversSourceConfig = (dataSource) =>
    DRIVERS_SOURCE_CONFIG[normalizeDriversSource(dataSource)];
